import { useState } from 'react';
import { Outlet } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
// hooks
import useSettings from '../../hooks/useSettings';
import { useSelector } from '../../redux/store';
import useResponsive from '../../hooks/useResponsive';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// config
import { HEADER, NAVBAR } from '../../config';
//
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import NavbarHorizontal from './navbar/NavbarHorizontal';
import { InternalCallModal, InternalCallWaitingModal } from '../../sections/internal-chat';

// ----------------------------------------------------------------------

const getUiConfig = (state) => state.uiConfig;

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ collapseClick, theme }) => ({
  flexGrow: 1,
  paddingTop: HEADER.MOBILE_HEIGHT + 24,
  paddingBottom: HEADER.MOBILE_HEIGHT + 24,
  [theme.breakpoints.up('lg')]: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    paddingBottom: 20,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && {
      marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    }),
  },
}));

// ----------------------------------------------------------------------

export default function DashboardLayout() {
  const { collapseClick, isCollapse } = useCollapseDrawer();

  const { themeLayout } = useSettings();

  const isDesktop = useResponsive('up', 'lg');

  const [open, setOpen] = useState(false);

  const verticalLayout = themeLayout === 'vertical';

  const { showStatsPanel } = useSelector(getUiConfig);
  const activeCallChatId = useSelector((state) => state.internalChat.call.chatId);

  const BASE_HEADER_HEIGHT = HEADER.DASHBOARD_DESKTOP_HEIGHT + 24;
  const COMPACT_PANEL_HEIGHT = 100;

  const DYNAMIC_HEADER_OFFSET = showStatsPanel ? BASE_HEADER_HEIGHT + COMPACT_PANEL_HEIGHT : BASE_HEADER_HEIGHT;

  if (verticalLayout) {
    return (
      <>
        <DashboardHeader onOpenSidebar={() => setOpen(false)} verticalLayout={verticalLayout} />
        {isDesktop ? (
          <NavbarHorizontal />
        ) : (
          <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
        )}

        <Box
          component="main"
          sx={{
            px: { lg: 2 },
            pt: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${DYNAMIC_HEADER_OFFSET}px`,
            },
            pb: {
              xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
              lg: `${DYNAMIC_HEADER_OFFSET - 24}px`,
            },
            transition: (theme) =>
              theme.transitions.create('padding-top', {
                duration: theme.transitions.duration.standard,
              }),
          }}
        >
          <Outlet />
        </Box>
        <InternalCallModal key={activeCallChatId || 'modal-empty'} />
        <InternalCallWaitingModal />
      </>
    );
  }

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
      }}
    >
      <DashboardHeader isCollapse={isCollapse} onOpenSidebar={() => setOpen(true)} />
      <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
      <MainStyle
        collapseClick={collapseClick}
        sx={{
          pt: { lg: `${DYNAMIC_HEADER_OFFSET}px` },
          transition: (theme) =>
            theme.transitions.create('padding-top', {
              duration: theme.transitions.duration.standard,
            }),
        }}
      >
        <Outlet />
      </MainStyle>
      <InternalCallModal key={activeCallChatId || 'modal-empty'} />
      <InternalCallWaitingModal />
    </Box>
  );
}
